/**
 * useWorkspaceRole / useCan — the one way to read the viewer's role in the
 * active workspace and gate UI on the shared permission matrix.
 *
 * The session comes from the App outlet context (`<Outlet context={{
 * session, ... }}>` in App.tsx), else from the shell's `AuthSessionContext`
 * (AppShell): the sheet and the Ask dock render beside the outlet, not inside
 * it, so the outlet context never reaches them. Components used to re-derive
 * the role by hand with the same memberships.find snippet; this hook replaces
 * those.
 *
 * `useWorkspaceRole` returns null when no session is available (standalone
 * mounts in tests, or before the session loads). `useCan` takes a `fallback`
 * for that case so call sites keep their existing default behavior — e.g.
 * decision buttons that default to visible because the backend enforces the
 * role either way.
 */
import { createContext, useContext } from "react";
import { useOutletContext } from "react-router-dom";
import { can } from "@yagni/shared";
import type {
  AuthSessionResponse,
  WorkspaceCapability,
  WorkspaceMembershipRole,
} from "@yagni/shared";

/**
 * The session for what the shell renders outside the routed `<Outlet />`: the
 * sheet. A ticket sheet is put in the shell's one sheet, a sibling of the
 * outlet, so the outlet context never reaches it; without this its reader read
 * as nobody and their own personal Team as one they are not on.
 */
export const AuthSessionContext = createContext<AuthSessionResponse | null>(null);

/** The session from the App outlet context, else the shell's, or null on standalone mounts. */
export function useAuthSession(): AuthSessionResponse | null {
  const ctx = useOutletContext<{ session?: AuthSessionResponse | null } | null>();
  const shell = useContext(AuthSessionContext);
  return ctx?.session ?? shell;
}

export function useWorkspaceRole(): WorkspaceMembershipRole | null {
  // Null-safe: standalone mounts (tests) have no outlet context.
  const session = useAuthSession();
  if (!session) return null;
  // A super admin in God Mode has no membership here, but the backend signs
  // the session in as "owner". In Act the backend accepts that owner's
  // writes, so admin pages (Repositories, Team settings, …) read as owner too.
  // Observe stays on the membership fallback: every write there is refused
  // ("Read-only: enable editing…"), and showing admin controls only to have
  // them refused is worse than the existing stub.
  if (session.godMode?.active === true && session.godMode.writeEnabled === true) return "owner";
  // A session read before its memberships landed (a standalone mount, a
  // test fixture) reads as a member, never as a crash.
  return (
    (session.memberships ?? []).find(
      (membership) => membership.workspace.id === session.workspace?.id,
    )?.role ?? "member"
  );
}

/**
 * Whether the viewer has `capability` under the shared matrix. `fallback`
 * is returned when the session is unavailable (tests / loading) — pick the
 * value that preserves the surface's pre-hook behavior.
 */
export function useCan(capability: WorkspaceCapability, fallback = false): boolean {
  const role = useWorkspaceRole();
  if (role === null) return fallback;
  return can(role, capability);
}

/**
 * Whether the viewer may read the workspace's Usage report and its drills
 * (`view-usage`). A god-mode session carries no membership, so its role
 * reads as "member", yet the backend serves the report to it as "owner".
 * Reading spend is read-only, so god mode counts as allowed here without
 * widening any other capability.
 */
export function useCanViewUsage(fallback = false): boolean {
  const session = useAuthSession();
  const canView = useCan("view-usage", fallback);
  return canView || session?.godMode?.active === true;
}

/**
 * Whether the viewer may manage this one Team, every field of it, its name
 * included: a workspace owner or admin (`manage-workers`) whoever created the
 * Team, or the Team's own owner (its publisher). The frontend half of the one
 * Team rule (`reachesEveryTeam` in the backend's `desks/visibility.ts`); God
 * Mode with Act on reads as owner here.
 *
 * A Team's legacy editor (`desks.editor_user_id`, which no screen sets) is left
 * out on purpose: `canManageDesk` still lets them save the charter through the
 * API, but most of what Team settings writes (repositories, people, where it
 * posts, the ladder, each line) takes the owner or `manage-workers`
 * (`requireCapabilityOrTeamOwner`), so opening the sheet to them would offer
 * fields the server refuses.
 *
 * Like `useCan(..., true)`, a standalone mount with no session reads as
 * allowed; the backend enforces either way.
 */
export function useCanManageTeam(team: { publisherUserId?: string | null } | null): boolean {
  const session = useAuthSession();
  const canManageWorkers = useCan("manage-workers", true);
  if (canManageWorkers) return true;
  const viewerId = session?.user?.id ?? null;
  return viewerId !== null && viewerId === (team?.publisherUserId ?? null);
}

/**
 * Coarse admin-or-owner check for surfaces that gate a whole admin area
 * rather than one capability. Prefer `useCan` for individual actions.
 */
export function useIsWorkspaceAdmin(fallback = false): boolean {
  const role = useWorkspaceRole();
  if (role === null) return fallback;
  return role === "admin" || role === "owner";
}
